import { Link } from "react-router-dom";
import { AlertTriangle, Loader2 } from "lucide-react";

const TONE = { red: "before:bg-rose-500", amber: "before:bg-amber-400" };

// One dashboard tile: a fixed frame whose body fills the space it is given; long lists scroll inside it.
export const Tile = ({ id, kicker, title, action, tone, index = 0, className = "", children }) => (
  <section
    data-testid={`tile-${id}`}
    style={{ animationDelay: `${index * 45}ms` }}
    className={`dash-rise relative flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-border bg-card p-3.5 xl:p-4
      before:absolute before:inset-y-3 before:left-0 before:w-[3px] before:rounded-r ${TONE[tone] || "before:bg-transparent"} ${className}`}
  >
    <header className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{kicker}</p>
        <h2 className="font-heading text-[15px] font-bold leading-tight truncate xl:text-base">{title}</h2>
      </div>
      {action}
    </header>
    <div className="mt-2.5 flex min-h-0 flex-1 flex-col">{children}</div>
  </section>
);

export const Stat = ({ testid, label, value, sub, tone }) => (
  <div className="min-w-0 rounded-xl bg-muted/50 px-3 py-2">
    <p className="truncate text-[11px] text-muted-foreground">{label}</p>
    <p data-testid={testid} className={`font-heading text-xl font-bold leading-tight tabular-nums xl:text-2xl ${tone === "red" ? "text-rose-600" : tone === "amber" ? "text-amber-600" : ""}`}>
      {value}
    </p>
    {sub && <p className="truncate text-[11px] text-muted-foreground">{sub}</p>}
  </div>
);

export const Row = ({ label, value, testid, tone, dense }) => (
  <div className={`flex items-baseline justify-between gap-3 border-b border-border/60 last:border-0 ${dense ? "py-1 text-[12.5px]" : "py-1.5 text-[13px]"}`}>
    <span className="shrink-0 text-muted-foreground">{label}</span>
    <span data-testid={testid} className={`min-w-0 truncate text-right font-medium tabular-nums ${tone === "red" ? "text-rose-600" : tone === "amber" ? "text-amber-600" : ""}`}>{value}</span>
  </div>
);

export const TileLink = ({ to, testid, children }) => (
  <Link to={to} data-testid={testid} className="shrink-0 rounded-full border border-border px-2.5 py-1 text-[11px] font-semibold text-accent-strong transition-colors hover:bg-accent hover:text-accent-foreground">
    {children}
  </Link>
);

export const Chip = ({ tone, children, testid }) => {
  const look = { red: "bg-rose-100 text-rose-700", amber: "bg-amber-100 text-amber-800", green: "bg-emerald-100 text-emerald-800",
    blue: "bg-sky-100 text-sky-800" }[tone] || "bg-muted text-muted-foreground";
  return <span data-testid={testid} className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${look}`}>{children}</span>;
};

// Loading / error / no-data states shared by every tile.
export const TileState = ({ state, children }) => {
  if (state.error && !state.data) {
    return (
      <p data-testid="tile-error" className="flex items-center gap-2 text-sm text-rose-600"><AlertTriangle className="h-4 w-4" /> {state.error}</p>
    );
  }
  if (!state.data) return <Loader2 data-testid="tile-loading" className="h-5 w-5 animate-spin text-muted-foreground" />;
  return children;
};

export const Empty = ({ testid, children }) => (
  <p data-testid={testid} className="grid flex-1 place-items-center rounded-xl border border-dashed border-border px-3 py-4 text-center text-xs text-muted-foreground">
    {children}
  </p>
);
